import type { Order, OrderItem } from "../../types";

/**
 * HA-768 / D-HA-24 — the Staff Terminal's order writes, one queue per order.
 *
 * <p><b>Per order, in order.</b> An order's writes run one at a time, each to completion, so its
 * create reaches the server before its first add-item (HA-754 run 11 caught the add 404ing on an
 * order not yet committed). Orders do not wait on each other: D-HA-24 24-2 says a stuck write on
 * one order never holds up another's.
 *
 * <p><b>Line writes</b> (create, add, edit, remove, and HA-265's restore) are what the operator sees as lines. One that
 * fails stops its order's queue: the writes behind it depend on it (an add after a failed create
 * would 404), so they are held, and the line shows "Not saved" with a Retry (24-1 step 3). Retry
 * runs the failed write again and then the rest.
 *
 * <p><b>Order writes</b> (save, send, complete) are awaited by a screen that shows their outcome,
 * so they never sit behind a failure waiting for a Retry: they are refused at once with
 * {@link UnsentChangesError}, and a failure of their own does not stop the queue.
 *
 * <p><b>Offline (the D-HA-24 carve-out).</b> A line write that fails while the device knows it is
 * offline does not raise an error: its order's queue pauses, its lines show "Not yet sent", and it
 * replays on reconnect. In memory only; keeping them across a reload is HA-164. Order writes are
 * refused offline like any other failure, so nothing shows as Paid or Completed unrecorded.
 */

export type WriteKind =
	"create" | "add" | "edit" | "remove" | "restore" | "order";

export interface OrderWrite {
	orderId: string;
	kind: WriteKind;
	/** The line this write is about: add, edit and remove. */
	itemId?: string;
	/** The line as it will be once this lands: add and edit. */
	item?: OrderItem;
	/** The order as created: create. */
	order?: Order;
	run: () => Promise<unknown>;
}

/**
 * "sent": the server has it. "deferred": the device is offline, and it will be sent on reconnect.
 * "discarded": the operator removed the line before it was ever sent.
 */
export type WriteOutcome = "sent" | "deferred" | "discarded";

/** What a line shows while its writes are outstanding. */
export type LineSyncStatus =
	"sending" | "removing" | "not-saved" | "not-removed" | "not-yet-sent";

/**
 * HA-265 — what an order shows while its restore (Undo, or Restore last removed item) is outstanding. The line is not on the
 * till until the server has put it back, so this shows on the order, not on a line.
 */
export type RestoreSyncStatus = "restoring" | "not-restored" | "not-yet-sent";

export type OrderSyncState = "idle" | "running" | "failed" | "paused";

export class UnsentChangesError extends Error {
	constructor() {
		super(
			"Some changes to this order haven't been sent yet. Retry or remove them first.",
		);
		this.name = "UnsentChangesError";
	}
}

interface Entry extends OrderWrite {
	attempted: boolean;
	settle: {
		resolve: (outcome: WriteOutcome) => void;
		reject: (error: unknown) => void;
	} | null;
}

interface Lane {
	orderId: string;
	entries: Entry[];
	state: OrderSyncState;
	error?: unknown;
}

const isLineWrite = (kind: WriteKind) => kind !== "order";

export class OrderWriteQueue {
	private lanes = new Map<string, Lane>();
	private listeners = new Set<() => void>();
	private version = 0;

	constructor(
		private readonly isOnline: () => boolean = () => navigator.onLine,
	) {}

	subscribe = (listener: () => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};

	getVersion = () => this.version;

	enqueue(write: OrderWrite): Promise<WriteOutcome> {
		const lane = this.lane(write.orderId);

		if (lane.state === "failed" || lane.state === "paused") {
			if (!isLineWrite(write.kind))
				return Promise.reject(new UnsentChangesError());

			// Saving the options editor again while its line is "Not saved": this is that write,
			// with the operator's latest input, so it replaces the failed one and retries it.
			const head = lane.entries[0];
			if (
				lane.state === "failed" &&
				head &&
				head.itemId === write.itemId &&
				(write.kind === "add" || write.kind === "edit") &&
				(head.kind === "add" || head.kind === "edit")
			) {
				return new Promise<WriteOutcome>((resolve, reject) => {
					lane.entries[0] = {
						...write,
						kind: head.kind,
						attempted: head.attempted,
						settle: { resolve, reject },
					};
					this.resume(lane);
				});
			}
		}

		return new Promise<WriteOutcome>((resolve, reject) => {
			const entry: Entry = {
				...write,
				attempted: false,
				settle: { resolve, reject },
			};
			lane.entries.push(entry);

			if (lane.state === "paused") {
				this.settle(entry, "deferred");
				this.changed();
				return;
			}

			this.changed();
			void this.pump(write.orderId, lane);
		});
	}

	/** Runs an order's failed write again, then the rest. */
	retry(orderId: string): Promise<WriteOutcome> {
		const lane = this.lanes.get(orderId);
		const head = lane?.entries[0];

		if (!lane || !head || lane.state !== "failed")
			return Promise.resolve("sent");

		return new Promise<WriteOutcome>((resolve, reject) => {
			head.settle = { resolve, reject };
			this.resume(lane);
		});
	}

	/** Reconnected: every paused order sends what it held. */
	online() {
		for (const lane of this.lanes.values()) {
			if (lane.state === "paused") this.resume(lane);
		}
	}

	/**
	 * The operator removed a line whose writes are still outstanding. Its writes that have not run
	 * are dropped, and so is a failed one. Returns whether the server may have the line anyway (a
	 * write for it reached the server and failed, perhaps after it landed), in which case the
	 * caller still sends a remove.
	 */
	discard(orderId: string, itemId: string): { mayExist: boolean } {
		const lane = this.lanes.get(orderId);
		if (!lane) return { mayExist: false };

		let mayExist = false;
		const running = lane.state === "running" ? lane.entries[0] : undefined;

		lane.entries = lane.entries.filter((entry) => {
			if (entry.itemId !== itemId || entry === running) return true;
			if (entry.attempted) mayExist = true;
			this.settle(entry, "discarded");
			return false;
		});

		if (running?.itemId === itemId) mayExist = true;

		if (lane.state === "failed" && lane.entries[0]?.attempted !== true) {
			// The failure is gone with the line it was about.
			this.resume(lane);
		} else {
			this.changed();
		}

		return { mayExist };
	}

	/** Drops an order's queue entirely: its create failed and the operator was told. */
	abandon(orderId: string) {
		const lane = this.lanes.get(orderId);
		if (!lane) return;

		for (const entry of lane.entries) this.settle(entry, "discarded");
		this.lanes.delete(orderId);
		this.changed();
	}

	state(orderId: string): {
		state: OrderSyncState;
		error?: unknown;
		failedKind?: WriteKind;
	} {
		const lane = this.lanes.get(orderId);
		if (!lane) return { state: "idle" };

		return {
			state: lane.state,
			error: lane.error,
			failedKind: lane.state === "failed" ? lane.entries[0]?.kind : undefined,
		};
	}

	/** The write an order's queue stopped at, if it stopped at one: what a Retry will send. */
	failedWrite(
		orderId: string,
	): Pick<OrderWrite, "kind" | "itemId" | "item"> | undefined {
		const lane = this.lanes.get(orderId);
		const head = lane?.entries[0];
		if (lane?.state !== "failed" || !head) return undefined;

		return { kind: head.kind, itemId: head.itemId, item: head.item };
	}

	/** True while an order has line writes the server has not confirmed. */
	hasUnsent(orderId: string) {
		const lane = this.lanes.get(orderId);
		return !!lane?.entries.some((entry) => isLineWrite(entry.kind));
	}

	/** HA-265: the state of an order's outstanding restore write, if it has one. */
	restoreStatus(orderId: string): RestoreSyncStatus | null {
		const lane = this.lanes.get(orderId);
		if (!lane) return null;

		const index = lane.entries.findIndex((entry) => entry.kind === "restore");
		if (index === -1) return null;

		if (lane.state === "paused") return "not-yet-sent";

		if (lane.state === "failed") {
			return index === 0 ? "not-restored" : "not-yet-sent";
		}

		return "restoring";
	}

	lineStatus(orderId: string, itemId: string): LineSyncStatus | null {
		const lane = this.lanes.get(orderId);
		if (!lane) return null;

		let index = -1;
		lane.entries.forEach((entry, i) => {
			if (entry.itemId === itemId) index = i;
		});
		const entry = lane.entries[index];
		if (!entry) return null;

		const removing = entry.kind === "remove";

		if (lane.state === "paused") return "not-yet-sent";

		if (lane.state === "failed") {
			if (index === 0) return removing ? "not-removed" : "not-saved";
			return "not-yet-sent";
		}

		return removing ? "removing" : "sending";
	}

	/**
	 * The lines an order has that the server does not yet, so a poll's copy of the order does not
	 * wipe them: every add or edit still outstanding, latest first per line.
	 */
	pendingLines(orderId: string): OrderItem[] {
		const lane = this.lanes.get(orderId);
		if (!lane) return [];

		const lines = new Map<string, OrderItem>();
		for (const entry of lane.entries) {
			if ((entry.kind === "add" || entry.kind === "edit") && entry.item) {
				lines.set(entry.item.id, entry.item);
			}
			if (entry.kind === "remove" && entry.itemId) {
				// A remove still waiting on the server keeps its line; only the add is superseded.
				lines.delete(entry.itemId);
			}
		}

		return [...lines.values()];
	}

	/** Orders that exist only here: created while offline, or still being created. */
	unsentOrders(): Order[] {
		const orders: Order[] = [];

		for (const lane of this.lanes.values()) {
			const create = lane.entries.find((entry) => entry.kind === "create");
			if (create?.order) orders.push(create.order);
		}

		return orders;
	}

	private lane(orderId: string): Lane {
		let lane = this.lanes.get(orderId);

		if (!lane) {
			lane = { orderId, entries: [], state: "idle" };
			this.lanes.set(orderId, lane);
		}

		return lane;
	}

	private resume(lane: Lane) {
		lane.state = "idle";
		lane.error = undefined;

		this.changed();
		void this.pump(lane.orderId, lane);
	}

	private async pump(orderId: string, lane: Lane) {
		if (lane.state !== "idle") return;

		const entry = lane.entries[0];
		if (!entry) {
			this.lanes.delete(orderId);
			this.changed();
			return;
		}

		lane.state = "running";
		entry.attempted = true;
		this.changed();

		try {
			await entry.run();
		} catch (error) {
			// Discarded or abandoned while it ran: nothing is waiting on this lane any more.
			if (this.lanes.get(orderId) !== lane) return;

			if (!isLineWrite(entry.kind)) {
				lane.entries.shift();
				lane.state = "idle";
				this.settle(entry, error, true);
				void this.pump(orderId, lane);
				return;
			}

			if (!this.isOnline()) {
				lane.state = "paused";
				for (const held of lane.entries) {
					if (isLineWrite(held.kind)) this.settle(held, "deferred");
				}
				this.refuseOrderWrites(lane);
				this.changed();
				return;
			}

			lane.state = "failed";
			lane.error = error;
			this.settle(entry, error, true);
			this.refuseOrderWrites(lane);
			this.changed();
			return;
		}

		if (this.lanes.get(orderId) !== lane) return;

		lane.entries.shift();
		lane.state = "idle";
		this.settle(entry, "sent");
		void this.pump(orderId, lane);
	}

	/** Order writes never wait behind a failure or an outage for a Retry that may not come. */
	private refuseOrderWrites(lane: Lane) {
		lane.entries = lane.entries.filter((entry) => {
			if (isLineWrite(entry.kind)) return true;
			this.settle(entry, new UnsentChangesError(), true);
			return false;
		});
	}

	private settle(entry: Entry, result: unknown, failed = false) {
		const settle = entry.settle;
		if (!settle) return;

		entry.settle = null;
		if (failed) settle.reject(result);
		else settle.resolve(result as WriteOutcome);
	}

	private changed() {
		this.version++;
		for (const listener of this.listeners) listener();
	}
}
